import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';
import { useFieldId } from '../lib/useId';

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  hint?: ReactNode;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, hint, className, id: providedId, ...props },
  ref,
) {
  const id = useFieldId(providedId);
  return (
    <div className="flex items-start gap-2">
      <input
        ref={ref}
        id={id}
        type="radio"
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0 border-line-strong accent-[hsl(var(--primary))]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
      <div className="flex flex-col">
        <label htmlFor={id} className="cursor-pointer select-none text-base text-ink">
          {label}
        </label>
        {hint && <span className="text-xs text-ink-muted">{hint}</span>}
      </div>
    </div>
  );
});

export interface RadioGroupOption {
  value: string;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps {
  /** Shared `name`. Without it the browser will not treat these as one group. */
  name: string;
  legend: ReactNode;
  options: RadioGroupOption[];
  value?: string;
  onChange?: (value: string) => void;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
}

/**
 * A radio group in a `<fieldset>`, which is what makes arrow-key navigation and the
 * group's accessible name work without any JavaScript on our part.
 */
export function RadioGroup({
  name,
  legend,
  options,
  value,
  onChange,
  hint,
  error,
  required,
  className,
}: RadioGroupProps) {
  return (
    <fieldset className={cn('flex flex-col gap-2', className)}>
      <legend className="mb-1 text-sm font-medium text-ink">
        {legend}
        {required && (
          <span className="ml-0.5 text-danger" aria-hidden="true">
            *
          </span>
        )}
      </legend>
      {hint && <p className="text-xs text-ink-muted">{hint}</p>}
      <div className="flex flex-col gap-2">
        {options.map((option) => (
          <Radio
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            hint={option.hint}
            disabled={option.disabled}
            checked={value === option.value}
            onChange={(event) => onChange?.(event.target.value)}
          />
        ))}
      </div>
      {error && (
        <p role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </fieldset>
  );
}
